// The page each place (Me, a workspace) was left on: going to a place from the
// rail (or from the Dock's menu of other workspaces) opens it there again,
// not at its home (the person asked for it, 2026-10-18).
//
// Promise:
// - Every page shown in Me and in each workspace is kept for its place (Me,
//   or the workspace's slug). Only the path: what a screen keeps beside it (a
//   tab, the search words) is not in the URL, and a search such as an OAuth
//   return marker must not come back.
// - Going to another place opens the page kept for it, or its home when none
//   is kept. Going to the place shown now goes to its home (as before).
// - Kept on this device (as the theme and the page a home screen app opens
//   on): a phone and a desktop show different things.
// - A kept page whose thing is gone shows that it is gone, as any old link
//   does, with the way back to its list.
import { defineDeviceSetting, useDeviceSetting } from "@trameria/ui-core";
import { inMePath, slugOfPath } from "./places";

/** Me's place key (no workspace slug can be "me": it is reserved). */
export const ME = "me";

/** The place a path belongs to: Me, a workspace's slug, or null. */
export const placeOfPath = (path: string): string | null =>
  inMePath(path) ? ME : slugOfPath(path);

type Pages = Readonly<Record<string, string>>;

const placePages = defineDeviceSetting<Pages>({
  key: "trameria-place-pages",
  parse(raw) {
    try {
      const kept: unknown = JSON.parse(raw ?? "{}");
      if (!kept || typeof kept !== "object") return {};
      // Only a page of the place it is kept for (nothing else is opened).
      return Object.fromEntries(
        Object.entries(kept).filter(
          (e): e is [string, string] =>
            typeof e[1] === "string" && placeOfPath(e[1]) === e[0],
        ),
      );
    } catch {
      return {};
    }
  },
  serialize: (pages) => JSON.stringify(pages),
});

/** Keeps a page shown (pages outside Me and the workspaces are not kept). */
export function keepPlacePage(path: string): void {
  const place = placeOfPath(path);
  const pages = placePages.read();
  if (!place || pages[place] === path) return;
  placePages.write({ ...pages, [place]: path });
}

/** Where going to `place` leads: the page kept for it, or `home`. */
export function usePlacePage(place: string, home: string): string {
  return useDeviceSetting(placePages)[0][place] ?? home;
}

/** The pages kept for every place (where going to each leads). */
export function usePlacePages(): Readonly<Record<string, string>> {
  return useDeviceSetting(placePages)[0];
}

/** The page kept for `place` (read once, for a press), or `home`. */
export const placePage = (place: string, home: string): string =>
  placePages.read()[place] ?? home;
